/**
 * Cometly pixel + journey token for Tagada CRM sign-up attribution.
 *
 * Same space ("TAGADA PAY") and uid as tagada.io and tagadapay.io: a visitor
 * who clicked an ad keeps one journey across them. The landings decorate
 * their links here with `?comet_token_override=<token>`; the pixel picks it
 * up, and the token is also kept in sessionStorage so it survives a blocked
 * pixel or the Google OAuth bounce. At sign-up it is written to
 * `unsafeMetadata.comet_token`, which the CRM (admin-app `crm-on-org-created`)
 * sends to Cometly with the "CRM Sign Up" event (`custom_event_14`).
 *
 * Loaded on the auth entry pages only: Cometly attributes the sign-up, it has
 * no use for a merchant's daily CRM page views.
 *
 * Links to the TagadaPay dashboard carry the journey on
 * (`lib/tagadapay-dashboard.ts`): without it the dashboard pixel starts a new
 * "referral from app.tagada.io" journey and the onboarding → live events lose
 * the ad that brought the merchant.
 */

const COMETLY_UID = '271a17-2251799817000002-9eead2-s';
const COMETLY_DOMAINS = [
  'tagada.io',
  'www.tagada.io',
  'app.tagada.io',
  'onboarding.tgdcare.com',
  'tagadapay.io',
  'dashboard.tagadapay.io',
].join(',');
export const COMETLY_PIXEL_SRC = `https://data.tagadapay.io/script.js?uid=${COMETLY_UID}&domains=${COMETLY_DOMAINS}`;

export const TOKEN_PARAM = 'comet_token_override';
const TOKEN_STORAGE_KEY = 'crm_comet_token';
const TOKEN_RE = /^[A-Za-z0-9._:-]{8,200}$/;
const ENTRY_PATHS = ['/sign-up', '/sign-up-new', '/sign-in', '/sso-callback'];

declare global {
  interface Window {
    /** Exposed by the Cometly pixel once loaded. */
    cometToken?: () => string | undefined;
  }
}

/** Same gate as the X pixel: Tagada's own host, never a partner white-label. */
export const isTagadaAdsHost = (host: string, brand: string | undefined): boolean => {
  if (brand && brand.toLowerCase() !== 'tagadapay') return false;
  return host === 'app.tagada.io' || host === 'localhost' || host.endsWith('.vercel.app');
};

/** Auth entry pages, or any page reached through a decorated landing link. */
export const isCometlyEntry = (pathname: string, search: string): boolean =>
  ENTRY_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`)) ||
  new URLSearchParams(search).has(TOKEN_PARAM);

/** A plausible journey token, trimmed — anything else is dropped. */
export const normalizeCometToken = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  return TOKEN_RE.test(value) ? value : null;
};

const shouldLoad = (): boolean =>
  typeof window !== 'undefined' &&
  isTagadaAdsHost(window.location.hostname, import.meta.env.VITE_BRAND as string | undefined) &&
  isCometlyEntry(window.location.pathname, window.location.search);

let didInit = false;

export const initCometlyPixel = (): void => {
  if (typeof document === 'undefined' || didInit || !shouldLoad()) return;
  didInit = true;
  const script = document.createElement('script');
  script.async = true;
  script.id = 'cometly-pixel';
  script.src = COMETLY_PIXEL_SRC;
  document.head.appendChild(script);
};

/** Keep the landing's journey token for this session (first value wins). */
export const persistCometTokenFromUrl = (): void => {
  if (typeof window === 'undefined') return;
  try {
    if (sessionStorage.getItem(TOKEN_STORAGE_KEY)) return;
    const value = normalizeCometToken(new URLSearchParams(window.location.search).get(TOKEN_PARAM));
    if (value) sessionStorage.setItem(TOKEN_STORAGE_KEY, value);
  } catch {
    // Private browsing / quota — best-effort only.
  }
};

/** The pixel's live token, else the one the landing link carried. */
export const getCometToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const live = normalizeCometToken(window.cometToken?.());
    if (live) return live;
  } catch {
    // Pixel not ready / blocked.
  }
  try {
    return normalizeCometToken(sessionStorage.getItem(TOKEN_STORAGE_KEY));
  } catch {
    return null;
  }
};

let userCometToken: string | null = null;

/**
 * The signed-in user's sign-up journey (`unsafeMetadata.comet_token`), kept in
 * memory once Clerk has loaded (`useAcquisitionTracking`). `null` signed out
 * or impersonated: a support session must not carry a merchant's journey.
 */
export const setUserCometToken = (raw: unknown): void => {
  userCometToken = normalizeCometToken(raw);
};

/**
 * The journey a link to the TagadaPay dashboard carries: the user's sign-up
 * journey first (the one the "CRM Sign Up" event was sent with, possibly days
 * ago), else this session's.
 */
export const getDashboardCometToken = (): string | null => userCometToken ?? getCometToken();
